// src/Context/AuthContext.tsx
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import {
  onAuthStateChanged,
  onIdTokenChanged,
  signOut,
  User,
} from 'firebase/auth';
import { doc, onSnapshot, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../Firebase/Firebase';
import type { CustomerFirestore } from '../Firebase/publicReads';
import { useCartStore } from '../stores/cartStore';
import { useFavoritesStore } from '../stores/favoritesStore';
import { Sentry } from '../sentry';

/**
 * Remonte dans le compte les favoris posés avant connexion.
 *
 * La plateforme se parcourt sans compte : les favoris s'accumulent alors dans
 * le navigateur (favoritesStore). À la première connexion — déclenchée au
 * moment de commander — on les recopie dans Firestore puis on vide le local,
 * qui n'a plus de raison d'être : Firestore devient la source de vérité.
 *
 * Best-effort : un échec réseau ne doit jamais bloquer la connexion, donc on
 * ne vide le local que si toutes les écritures ont abouti.
 */
const mergeLocalFavorites = async (uid: string) => {
  const local = useFavoritesStore.getState().items;
  if (local.length === 0) return;
  try {
    await Promise.all(
      local.map((f) =>
        setDoc(
          doc(db, 'Customers', uid, 'Favorites', f.productId),
          {
            productId:    f.productId,
            productName:  f.productName,
            productImage: f.productImage,
            price:        f.price,
            addedAt:      serverTimestamp(),
          },
          // merge : ne pas écraser un favori déjà présent côté serveur
          { merge: true },
        )
      )
    );
    useFavoritesStore.getState().clear();
  } catch (err) {
    console.error('[auth] fusion des favoris locaux échouée', err);
  }
};

// Observabilité Auth — trace les transitions de session (+ réseau) pour
// diagnostiquer les déconnexions au changement de réseau. Va dans la console ET
// en breadcrumb Sentry (no-op si VITE_SENTRY_DSN absent). PII non logué (uid seul).
const authBreadcrumb = (message: string, level: 'info' | 'warning', data?: Record<string, unknown>) => {
  const online = typeof navigator !== 'undefined' ? navigator.onLine : null;
  Sentry.addBreadcrumb({ category: 'auth', level, message, data: { ...data, online } });
  // eslint-disable-next-line no-console
  console.log(`[auth] ${message}`, { ...data, online });
};

interface CustomerData {
  id: string;
  displayName?: string;
  email?: string;
  phone?: string;
  photoURL?: string;
  address?: string | null;
  city?: string | null;
  quartier?: string | null;
  profileLevel?: 'none' | 'basic' | 'complete';
}

interface AuthContextValue {
  user: User | null;
  customer: CustomerData | null;
  profileLevel: 'none' | 'basic' | 'complete';
  loading: boolean;
  refreshCustomer: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [profileLevel, setProfileLevel] = useState<'none' | 'basic' | 'complete'>('none');
  const [loading, setLoading] = useState(true);
  // Évite de rejouer la fusion à chaque rafraîchissement de token.
  const mergedForUid = useRef<string | null>(null);

  useEffect(() => {
    // Persistance déjà configurée à la création de `auth` (initializeAuth
    // multi-niveaux dans Firebase.ts) — on attache directement le listener,
    // sans course async qui pouvait éjecter l'utilisateur au cold-start.
    authBreadcrumb('AuthProvider mounted', 'info'); // mount = boot/cold reload

    const unsubAuth = onAuthStateChanged(auth, (u) => {
      authBreadcrumb(
        u ? 'state: signed-in' : 'state: signed-out',
        u ? 'info' : 'warning',
        { uid: u?.uid ?? null },
      );
      setUser(u);
      if (u) {
        if (mergedForUid.current !== u.uid) {
          mergedForUid.current = u.uid;
          void mergeLocalFavorites(u.uid);
        }
      } else {
        mergedForUid.current = null;
        setCustomer(null);
        setProfileLevel('none');
        setLoading(false);
      }
    });

    // Rotation du token (refresh) — utile pour corréler une perte de session
    // avec un échec de refresh juste après un changement de réseau.
    const unsubToken = onIdTokenChanged(auth, (u) => {
      if (u) authBreadcrumb('idToken refreshed', 'info', { uid: u.uid });
    });

    // Transitions réseau — pour confirmer le scénario « offline → online → null ».
    const onOnline  = () => authBreadcrumb('network: online',  'info',    { currentUser: auth.currentUser?.uid ?? null });
    const onOffline = () => authBreadcrumb('network: offline', 'warning', { currentUser: auth.currentUser?.uid ?? null });
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      unsubAuth();
      unsubToken();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    const ref = doc(db, 'Customers', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data() as CustomerFirestore;
        setCustomer({ id: snap.id, ...data } as CustomerData);
        setProfileLevel(data.profileLevel || 'basic');
      } else {
        setCustomer(null);
        setProfileLevel('none');
      }
      setLoading(false);
    }, (err) => {
      console.error('AuthContext snapshot error:', err);
      setLoading(false);
    });
    return () => unsub();
  }, [user]);

  const refreshCustomer = useCallback(async () => {
    if (!user) return;
    const snap = await getDoc(doc(db, 'Customers', user.uid));
    if (snap.exists()) {
      const data = snap.data() as CustomerFirestore;
      setCustomer({ id: snap.id, ...data } as CustomerData);
      setProfileLevel(data.profileLevel || 'basic');
    }
  }, [user]);

  const logout = useCallback(async () => {
    useCartStore.getState().clearCart();
    await signOut(auth);
    setUser(null);
    setCustomer(null);
    setProfileLevel('none');
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, customer, profileLevel, loading, refreshCustomer, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans <AuthProvider>');
  return ctx;
};